import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, X, Trash2, Gauge, Eye } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import { UpgradeWall } from '@/components/app/UpgradeWall';
import { isPlanGateError } from '@/lib/planGate';
import { kpiApi, Kpi, KpiInput, KpiOperand, KpiFormat, formatKpiValue } from '@/lib/kpis';
import { forecastsApi } from '@/lib/forecasts';

const today = new Date().toISOString().slice(0, 10);
const oneYearAgo = `${Number(today.slice(0, 4)) - 1}${today.slice(4)}`;
const FORMATS: KpiFormat[] = ['number', 'percent', 'currency'];
const FORMAT_PILL: Record<KpiFormat, string> = { number: 'bg-ink-100 text-ink-600', percent: 'bg-violet-50 text-violet-700', currency: 'bg-emerald-50 text-emerald-700' };

export function KpiPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const kpis = useQuery({ queryKey: ['kpis', wsId], queryFn: () => kpiApi.list(wsId!), enabled: !!wsId, retry: false });
  const planGated = kpis.error && isPlanGateError(kpis.error);

  return (
    <AppShell title={t('kpi.title', { defaultValue: 'KPIs' })} subtitle={t('kpi.subtitle', { defaultValue: 'Define metrics as formulas over your accounts.' })}>
      <main className="mx-auto max-w-4xl px-6 py-10 space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('kpi.title', { defaultValue: 'KPIs' })} <span className="text-muted-foreground text-lg">経営指標</span></h1>
            <p className="mt-1 text-ink-600">{t('kpi.subtitle', { defaultValue: 'Define named metrics as formulas over account totals (e.g. gross margin %, revenue per head). They appear as tiles on the dashboard.' })}</p>
          </div>
          {!planGated && <button onClick={() => setShowForm((v) => !v)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"><Plus className="h-4 w-4" /> {t('kpi.new', { defaultValue: 'New KPI' })}</button>}
        </div>

        {planGated && wsId && <UpgradeWall wsId={wsId} requiredTier="growth" featureName={t('kpi.feature_name', { defaultValue: 'KPI registry' })} />}
        {showForm && wsId && <NewKpiForm wsId={wsId} onClose={() => setShowForm(false)} onDone={() => { qc.invalidateQueries({ queryKey: ['kpis', wsId] }); qc.invalidateQueries({ queryKey: ['kpi-dashboard', wsId] }); setShowForm(false); }} t={t} />}

        {!planGated && (
          kpis.isLoading ? <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
            : (kpis.data ?? []).length === 0 ? (
              <div className="rounded-md border-2 border-dashed border-border bg-white p-10 text-center">
                <Gauge className="h-6 w-6 text-ink-300 mx-auto mb-2" />
                <p className="text-ink-700 font-medium">{t('kpi.empty', { defaultValue: 'No KPIs defined yet.' })}</p>
              </div>
            ) : (
              <div className="space-y-2">{(kpis.data ?? []).map((k) => <KpiRow key={k.id} wsId={wsId!} k={k} t={t} />)}</div>
            )
        )}
      </main>
    </AppShell>
  );
}

type TFn = (k: string, o?: Record<string, unknown>) => string;

function KpiRow({ wsId, k, t }: { wsId: string; k: Kpi; t: TFn }) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['kpis', wsId] }); qc.invalidateQueries({ queryKey: ['kpi-dashboard', wsId] }); };
  const del = useMutation({ mutationFn: () => kpiApi.remove(wsId, k.id), onSuccess: invalidate });
  return (
    <div className="rounded-md border border-border bg-white px-4 py-3 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-ink-900 truncate">{k.name}</p>
          <span className={`text-[10px] uppercase tracking-wide font-semibold rounded px-1.5 py-0.5 ${FORMAT_PILL[k.format]}`}>{k.format}</span>
        </div>
        <p className="text-[12px] text-muted-foreground num mt-0.5 truncate">{k.formula} · {k.scenario}{k.target !== null ? ` · target ${k.target}` : ''}</p>
      </div>
      {!confirm ? (
        <button onClick={() => setConfirm(true)} className="text-ink-400 hover:text-warn-600 p-1 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
      ) : (
        <span className="inline-flex items-center gap-2 text-xs shrink-0">
          <button onClick={() => del.mutate()} disabled={del.isPending} className="text-warn-700 font-semibold hover:underline">{del.isPending ? t('common.loading') : t('common.yes')}</button>
          <button onClick={() => setConfirm(false)} className="text-ink-500 hover:underline">{t('common.no')}</button>
        </span>
      )}
    </div>
  );
}

function NewKpiForm({ wsId, onClose, onDone, t }: { wsId: string; onClose: () => void; onDone: () => void; t: TFn }) {
  const { data: scenarios = [] } = useQuery({ queryKey: ['scenarios', wsId], queryFn: () => forecastsApi.scenarios(wsId) });
  const scenarioOpts = useMemo(() => Array.from(new Set(['actual', 'budget', ...scenarios.map((s) => s.scenario)])), [scenarios]);
  const [name, setName] = useState('');
  const [scenario, setScenario] = useState('actual');
  const [format, setFormat] = useState<KpiFormat>('percent');
  const [target, setTarget] = useState('');
  const [formula, setFormula] = useState('');
  const [operands, setOperands] = useState<OperandDraft[]>([{ key: 'rev', kind: 'account', accountCodes: '' }]);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const input = 'w-full rounded-lg border border-border bg-white px-3 py-2 text-sm';

  const build = (): KpiInput => ({
    name: name.trim(), scenario, format, target: target.trim() ? Number(target) : null, formula: formula.trim(),
    operands: operands.map(toOperand),
  });

  const save = useMutation({ mutationFn: () => kpiApi.create(wsId, build()), onSuccess: onDone, onError: (e) => setErr(errMsg(e)) });
  const runPreview = useMutation({
    mutationFn: () => kpiApi.preview(wsId, { ...build(), periodStart: oneYearAgo, periodEnd: today, bucket: 'none' }),
    onSuccess: (r) => { setErr(null); setPreview(formatKpiValue(r.current, r.format, r.reportingCurrency)); },
    onError: (e) => { setPreview(null); setErr(errMsg(e)); },
  });

  return (
    <div className="rounded-md border border-border bg-white p-5 space-y-4">
      <h3 className="text-sm font-semibold text-ink-700">{t('kpi.new', { defaultValue: 'New KPI' })}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Lbl label={t('kpi.name', { defaultValue: 'Name' })}><input value={name} onChange={(e) => setName(e.target.value)} className={input} placeholder="Gross margin %" /></Lbl>
        <Lbl label={t('kpi.scenario', { defaultValue: 'Scenario' })}><select value={scenario} onChange={(e) => setScenario(e.target.value)} className={input}>{scenarioOpts.map((s) => <option key={s} value={s}>{s}</option>)}</select></Lbl>
        <Lbl label={t('kpi.format', { defaultValue: 'Format' })}><select value={format} onChange={(e) => setFormat(e.target.value as KpiFormat)} className={input}>{FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}</select></Lbl>
        <Lbl label={t('kpi.target', { defaultValue: 'Target (optional)' })}><input value={target} onChange={(e) => setTarget(e.target.value.replace(/[^0-9.\-]/g, ''))} className={`num ${input}`} placeholder="0.4" /></Lbl>
      </div>

      <div>
        <span className="block text-xs font-semibold text-ink-700 mb-1">{t('kpi.operands', { defaultValue: 'Operands (reference these keys in the formula)' })}</span>
        <div className="space-y-2">
          {operands.map((o, i) => (
            <div key={i} className="flex items-center gap-2 flex-wrap">
              <input value={o.key} onChange={(e) => setOperands((os) => os.map((x, j) => j === i ? { ...x, key: e.target.value } : x))} placeholder="key" className={`num ${input} max-w-[90px]`} />
              <select value={o.kind} onChange={(e) => setOperands((os) => os.map((x, j) => j === i ? { ...x, kind: e.target.value as OperandDraft['kind'] } : x))} className={`${input} max-w-[130px]`}>
                <option value="account">account</option>
                <option value="headcount">headcount</option>
              </select>
              {o.kind === 'account' ? (
                <input value={o.accountCodes ?? ''} onChange={(e) => setOperands((os) => os.map((x, j) => j === i ? { ...x, accountCodes: e.target.value } : x))} placeholder={t('kpi.codes_ph', { defaultValue: 'account codes, comma-separated' })} className={`num ${input} flex-1 min-w-[160px]`} />
              ) : (
                <input value={o.department ?? ''} onChange={(e) => setOperands((os) => os.map((x, j) => j === i ? { ...x, department: e.target.value } : x))} placeholder={t('kpi.dept_ph', { defaultValue: 'department (optional)' })} className={`${input} flex-1 min-w-[160px]`} />
              )}
              <button onClick={() => setOperands((os) => os.filter((_, j) => j !== i))} className="text-ink-400 hover:text-warn-600 p-1"><X className="h-4 w-4" /></button>
            </div>
          ))}
          <button onClick={() => setOperands((os) => [...os, { key: '', kind: 'account', accountCodes: '' }])} className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> {t('kpi.add_operand', { defaultValue: 'Add operand' })}</button>
        </div>
      </div>

      <Lbl label={t('kpi.formula', { defaultValue: 'Formula (use operand keys + - * / and parentheses)' })}>
        <input value={formula} onChange={(e) => setFormula(e.target.value)} placeholder="(rev - cogs) / rev" className={`num ${input}`} />
      </Lbl>

      {preview !== null && <p className="text-sm text-emerald-700 num">{t('kpi.preview_result', { defaultValue: 'Preview' })}: <span className="font-semibold">{preview}</span></p>}
      {err && <p className="text-xs text-warn-700">{err}</p>}
      <div className="flex gap-2">
        <button onClick={() => save.mutate()} disabled={save.isPending || !name.trim() || !formula.trim()} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">{save.isPending ? t('common.loading') : t('kpi.create', { defaultValue: 'Create KPI' })}</button>
        <button onClick={() => runPreview.mutate()} disabled={runPreview.isPending || !formula.trim()} className="px-4 py-2 rounded-lg border border-border text-sm text-ink-700 hover:bg-foreground/[0.04] inline-flex items-center gap-1.5 disabled:opacity-50"><Eye className="h-4 w-4" /> {t('kpi.preview', { defaultValue: 'Preview' })}</button>
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-border text-sm text-ink-700 hover:bg-foreground/[0.04]">{t('common.cancel')}</button>
      </div>
    </div>
  );
}

interface OperandDraft { key: string; kind: 'account' | 'headcount'; accountCodes?: string; department?: string }
function toOperand(o: OperandDraft): KpiOperand {
  if (o.kind === 'headcount') return { key: o.key.trim(), kind: 'headcount', department: o.department?.trim() || undefined };
  return { key: o.key.trim(), kind: 'account', accountCodes: (o.accountCodes ?? '').split(',').map((s) => s.trim()).filter(Boolean) };
}
function errMsg(e: unknown): string {
  return ((e as { response?: { data?: { message?: string } } })?.response?.data?.message) ?? (e as Error).message;
}
function Lbl({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="block text-xs font-semibold text-ink-700 mb-1">{label}</span>{children}</label>;
}
